import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, MapPin, Clock, ShieldCheck, Phone, Car, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { apiClient } from "../lib/api";
import usePushNotifications from "../hooks/usePushNotifications";
import TPGoogleMap from "../components/TPGoogleMap";

const STATUS_LABEL = {
  awaiting_va: "Request received — alerting dispatch",
  claimed: "Dispatcher reviewing your request",
  assigned: "Driver assigned",
  en_route: "Driver en route to pickup",
  arrived: "Driver has arrived",
  onboard: "En route to drop-off",
  arrived_dropoff: "Arrived at drop-off",
  completed: "Trip completed",
  cancelled: "Request cancelled",
};

const STATUS_TONE = {
  awaiting_va: "slate", claimed: "amber", assigned: "blue", en_route: "green",
  arrived: "violet", onboard: "blue", arrived_dropoff: "violet",
  completed: "green", cancelled: "red",
};

const TONE_STYLES = {
  slate: "bg-slate-100 text-slate-700",
  amber: "bg-amber-100 text-amber-800",
  blue: "bg-blue-100 text-blue-800",
  green: "bg-green-100 text-green-800",
  violet: "bg-violet-100 text-violet-800",
  red: "bg-red-100 text-red-800",
};

const TERMINAL = ["completed", "cancelled"];

function useCountdown(deadline) {
  const [msLeft, setMsLeft] = useState(null);
  useEffect(() => {
    if (!deadline) { setMsLeft(null); return; }
    const tick = () => setMsLeft(new Date(deadline).getTime() - Date.now());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [deadline]);
  return msLeft;
}

/** Public CPD tracking page — one link, texted/emailed the moment a call ends,
 * that stays valid through claim → assign → completion. Renders the same
 * "Driver Beacon" style live map + driver card used on the regular /r/{token}
 * rider tracker once a driver is assigned and en route. Independent of the
 * regular NEMT /r/{token} rider-app tracker (see cpd_program.py /track). */
export default function CPDTrack() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const push = usePushNotifications({ riderToken: token });

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/cpd/track/${token}`);
      setData(data);
      setError(null);
    } catch (err) {
      // Only a real 404 means the link itself is bad. A transient network/server
      // hiccup on one of the 8s polls must NOT permanently flip this page to
      // "invalid or expired" — that was the root cause of officers seeing a dead
      // link on a perfectly valid, never-expiring tracking token.
      if (err?.response?.status === 404) setError("not_found");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [token]);

  const enablePush = async () => {
    const ok = await push.enable();
    if (ok) toast.success("Live updates enabled");
    else if (push.error) toast.error(push.error);
  };

  const msLeft = useCountdown(data?.sla_deadline);

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="cpd-track-error">This tracking link is invalid or has expired.</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const tone = STATUS_TONE[data.status] || "slate";
  const isTerminal = TERMINAL.includes(data.status);
  const showMap = data.pickup?.lat && data.dropoff?.lat;
  const driverName = data.assigned_driver?.name;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4">
      <div className="max-w-md w-full space-y-5" data-testid="cpd-track-page">
        {data.branding?.logo_url ? (
          <div className="flex items-center gap-2" data-testid="cpd-track-tp-branding">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${data.branding.logo_url}`} alt={data.branding.tp_name} className="h-8 w-8 rounded-md object-contain" />
            <span className="font-bold text-sm uppercase tracking-widest" style={data.branding.primary_color ? { color: data.branding.primary_color } : undefined}>
              {data.branding.tp_name}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-forest font-bold text-sm uppercase tracking-widest">
            <ShieldCheck size={18} /> Medex CPD Transport
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${TONE_STYLES[tone]}`} data-testid="cpd-track-status">
            {isTerminal ? (data.status === "completed" ? <CheckCircle2 size={14} /> : <XCircle size={14} />) : <Loader2 size={14} className="animate-spin" />}
            {STATUS_LABEL[data.status] || data.status}
          </div>
          {data.case_number && <div className="text-xs text-slate-500">Case {data.case_number}</div>}
          {msLeft != null && !isTerminal && (
            <div className="flex items-center gap-2 text-sm text-amber-700" data-testid="cpd-track-sla">
              <Clock size={14} /> {msLeft > 0 ? `${Math.floor(msLeft / 60000)} min to SLA` : "SLA window passed"}
            </div>
          )}
          {data.eta_min != null && data.status === "en_route" && (
            <div className="flex items-center gap-2 text-sm font-semibold text-forest" data-testid="cpd-track-eta">
              <Clock size={14} /> Estimated arrival: {data.eta_min} min
            </div>
          )}
        </div>

        {showMap && (
          <div className="rounded-2xl overflow-hidden" data-testid="cpd-track-map">
            <TPGoogleMap
              pickup={data.pickup}
              dropoff={data.dropoff}
              drivers={data.driver_gps?.lat ? [{
                driver_id: "d",
                name: driverName || "Driver",
                last_seen_lat: data.driver_gps.lat,
                last_seen_lng: data.driver_gps.lng,
                shift_status: "on_shift",
              }] : []}
              height={280}
              emptyLabel="Waiting for driver location…"
            />
          </div>
        )}

        {driverName && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="cpd-track-driver">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">Your driver</div>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-forest text-white grid place-items-center font-bold text-lg">
                {driverName[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="font-bold text-slate-800">{driverName}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Car size={11} /> {data.assigned_driver?.vehicle_type || "sedan"}
                </div>
              </div>
              <a href="tel:+18336592699" className="p-2 rounded-full bg-forest text-white" aria-label="Call dispatch" data-testid="cpd-track-call-dispatch">
                <Phone size={16} />
              </a>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Trip route</div>
          <div className="flex items-start gap-2">
            <MapPin size={16} className="text-green-600 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Pickup</div>
              <div className="text-sm text-slate-700">{data.pickup?.address}</div>
            </div>
          </div>
          <div className="border-l-2 border-dashed border-slate-300 ml-2 h-4" />
          <div className="flex items-start gap-2">
            <MapPin size={16} className="text-red-600 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Drop-off</div>
              <div className="text-sm text-slate-700">{data.dropoff?.address}</div>
            </div>
          </div>
        </div>

        {push.supported && push.permission !== "granted" && (
          <button
            onClick={enablePush}
            disabled={push.enabling}
            className="w-full flex items-center justify-center gap-2 bg-white border border-forest text-forest rounded-xl py-3 text-sm font-semibold disabled:opacity-50"
            data-testid="cpd-track-enable-push-btn"
          >
            <BellRing size={16} /> {push.enabling ? "Enabling…" : "Enable live updates on this device"}
          </button>
        )}
        {push.permission === "granted" && (
          <div className="text-center text-xs text-green-700 font-semibold" data-testid="cpd-track-push-enabled">
            Live updates enabled ✓
          </div>
        )}

        <div className="text-center text-xs text-slate-500">
          Need help? Call dispatch at <a href="tel:+18336592699" className="text-forest font-semibold underline">1-833-659-2699</a>
        </div>
      </div>
    </div>
  );
}
